// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Plan Fit Score (pre-hook screen, docs/product/paywall-prehook-concepts.md,
 * concept B — owner-approved 2026-08-30). Grades the user's ACTUAL generated
 * first week against what they told us during onboarding: four sub-scores of
 * 25, each with a receipt derived from real plan data. HARD RULE: the score
 * must be reproducible from its inputs — nothing is estimated, inflated, or
 * invented, and missing user limits score as honestly-full ("no limit set"
 * trivially fits) with a receipt that says so.
 *
 * Mirrored in mobile/src/lib/fitScore.ts — keep the logic identical.
 */

export interface FitScoreMeal {
  recipeId: string
  slot: string
  activeTimeMinutes: number
  /** Per-serving estimate; null = no estimate exists (skipped in the fallback sum, never counted as free). */
  estimatedCostCents: number | null
  servings: number
}

export interface FitScoreInput {
  /** Server-validated calorie target (bounds + floors enforced there). */
  calorieTargetKcal: number | null
  /** plan.lastValidation.status — "PASS" is the only applied-plan state. */
  validationStatus: string
  meals: FitScoreMeal[]
  /** Step-9 answer; null = user set no per-meal time limit. */
  timePerMealMinutes: number | null
  /** Step 2: the server's budget verdict when available — the SAME cost
   * model as generation/Shop; overrides the consumption-based estimate. */
  budgetVerdict?: {
    status: "no_target" | "within_budget" | "over_budget" | "unverifiable"
    weeklyCostCents: number
    budgetCents: number | null
    gapCents: number | null
  } | null
  /** Weekly grocery budget in cents; null = no budget target. */
  weeklyBudgetEurCents: number | null
}

export type FitReceipt =
  | { kind: "safetyOk"; kcal: number }
  | { kind: "safetyMissing" }
  | { kind: "timeOk"; avg: number; limit: number }
  | { kind: "timeOver"; avg: number; limit: number }
  | { kind: "timeNoLimit" }
  | { kind: "budgetOk"; underCents: number }
  | { kind: "budgetOver"; overCents: number }
  | { kind: "budgetNoLimit" }
  | { kind: "tastePass"; distinctDishes: number }
  | { kind: "tastePending" }

export interface FitSubScore {
  key: "safety" | "time" | "budget" | "taste"
  points: number
  max: 25
  receipt: FitReceipt
}

export interface FitScore {
  total: number
  subs: FitSubScore[]
  /** Total cost of the week in cents (servings-weighted), for display. */
  weekCostCents: number
}

const SNACK_SLOTS = new Set(["morning_snack", "afternoon_snack", "snack"])

/** Returns null when there is no plan to grade (no meals). */
export function computeFitScore(input: FitScoreInput): FitScore | null {
  if (input.meals.length === 0) return null

  const subs: FitSubScore[] = []

  // Safety & feasibility: the target exists ⇒ the server accepted it inside
  // its capped, floor-checked band (profileTargets) — that check IS the
  // sub-score; without a target the plan pipeline fails closed anyway.
  subs.push(
    input.calorieTargetKcal !== null
      ? { key: "safety", points: 25, max: 25, receipt: { kind: "safetyOk", kcal: input.calorieTargetKcal } }
      : { key: "safety", points: 0, max: 25, receipt: { kind: "safetyMissing" } }
  )

  // Time fit: average hands-on minutes of MAIN meals vs the stated limit.
  const mains = input.meals.filter(m => !SNACK_SLOTS.has(m.slot))
  const avgActive =
    mains.length > 0
      ? Math.round(mains.reduce((acc, m) => acc + m.activeTimeMinutes, 0) / mains.length)
      : 0
  if (input.timePerMealMinutes === null) {
    subs.push({ key: "time", points: 25, max: 25, receipt: { kind: "timeNoLimit" } })
  } else if (avgActive <= input.timePerMealMinutes) {
    subs.push({
      key: "time",
      points: 25,
      max: 25,
      receipt: { kind: "timeOk", avg: avgActive, limit: input.timePerMealMinutes },
    })
  } else {
    subs.push({
      key: "time",
      points: Math.max(5, Math.round((25 * input.timePerMealMinutes) / avgActive)),
      max: 25,
      receipt: { kind: "timeOver", avg: avgActive, limit: input.timePerMealMinutes },
    })
  }

  // Budget fit: the server verdict when present (one cost model — Step 2),
  // else the servings-weighted consumption estimate as fallback.
  const verdict = input.budgetVerdict ?? null
  const weekCostCents = verdict
    ? verdict.weeklyCostCents
    : input.meals.reduce((acc, m) => acc + (m.estimatedCostCents ?? 0) * m.servings, 0)
  const budgetTarget = verdict ? verdict.budgetCents : input.weeklyBudgetEurCents
  if (budgetTarget === null) {
    subs.push({ key: "budget", points: 25, max: 25, receipt: { kind: "budgetNoLimit" } })
  } else if (verdict?.status === "unverifiable") {
    // Criterion 3: partial pricing never claims full budget compliance —
    // honest middle score, no fabricated certainty either way.
    subs.push({
      key: "budget",
      points: 15,
      max: 25,
      receipt: { kind: "budgetOk", underCents: Math.max(0, budgetTarget - weekCostCents) },
    })
  } else if (weekCostCents <= budgetTarget) {
    subs.push({
      key: "budget",
      points: 25,
      max: 25,
      receipt: { kind: "budgetOk", underCents: budgetTarget - weekCostCents },
    })
  } else {
    subs.push({
      key: "budget",
      points: Math.max(5, Math.round((25 * budgetTarget) / weekCostCents)),
      max: 25,
      receipt: { kind: "budgetOver", overCents: weekCostCents - budgetTarget },
    })
  }

  // Taste & rules: validator PASS carries the allergen/diet guarantee (15);
  // variety is the distinct-dish share of the week (10).
  const distinct = new Set(input.meals.map(m => m.recipeId)).size
  if (input.validationStatus === "PASS") {
    const varietyPoints = Math.round((10 * distinct) / input.meals.length)
    subs.push({
      key: "taste",
      points: 15 + varietyPoints,
      max: 25,
      receipt: { kind: "tastePass", distinctDishes: distinct },
    })
  } else {
    subs.push({ key: "taste", points: 0, max: 25, receipt: { kind: "tastePending" } })
  }

  return {
    total: subs.reduce((acc, s) => acc + s.points, 0),
    subs,
    weekCostCents,
  }
}
